import { useEffect, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import type { Promotion } from '@dnyaneshwari/shared';
import {
  IMAGE_WARN_BYTES, clearPromotion, getPromotion, isoToLocal, localToIso,
  readAspectRatio, savePromotion, uploadPromotionImage,
} from '@/services/promotionService';

type Draft = Omit<Promotion, 'version'>;

function blank(): Draft {
  const now = new Date();
  const week = new Date(now.getTime() + 7 * 864e5);
  return {
    id: `promo-${now.toISOString().slice(0, 10)}-${Math.random().toString(36).slice(2, 7)}`,
    name: '',
    image: { mr: '' },
    startAt: now.toISOString(),
    endAt: week.toISOString(),
    active: false,
    link: '',
  };
}

/**
 * The full-screen promotion the app shows on open.
 *
 * One promotion runs at a time. Saving replaces whatever is live, and
 * "Remove" takes it down. Both take effect on each person's next launch --
 * the app reads this once per open, so nothing here reaches a phone that is
 * already running.
 *
 * The id is what dismissal is remembered against. Editing a running
 * promotion's picture without changing its id leaves it hidden for everyone
 * who has already closed it, so "New promotion" mints a fresh one.
 */
export default function PromotionPage() {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [live, setLive] = useState<Promotion | null>(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [preview, setPreview] = useState<'mr' | 'en' | null>(null);

  useEffect(() => {
    void getPromotion()
      .then((p) => {
        setLive(p);
        setDraft(p ? { ...p } : blank());
      })
      .catch(() => setDraft(blank()));
  }, []);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  async function upload(lang: 'mr' | 'en', file: File) {
    if (!draft) return;
    if (file.size > IMAGE_WARN_BYTES) {
      const mb = (file.size / 1e6).toFixed(1);
      if (!confirm(`This picture is ${mb} MB. Every device downloads it once, so a large file costs real bandwidth. Upload anyway?`)) return;
    }
    setBusy(true);
    setStatus('');
    try {
      const url = await uploadPromotionImage(draft.id, lang, file, (pct) =>
        setProgress((p) => ({ ...p, [lang]: pct }))
      );
      const ratio = lang === 'mr' ? await readAspectRatio(file) : draft.aspectRatio;
      setDraft((d) => (d ? { ...d, image: { ...d.image, [lang]: url }, aspectRatio: ratio } : d));
      setStatus(`${lang.toUpperCase()} picture uploaded.`);
    } catch (e) {
      setStatus(`Upload failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
      setProgress((p) => ({ ...p, [lang]: 0 }));
    }
  }

  async function save() {
    if (!draft) return;
    if (!draft.image.mr) return setStatus('A Marathi picture is required.');
    if (Date.parse(draft.endAt) <= Date.parse(draft.startAt)) return setStatus('The end must be after the start.');
    setBusy(true);
    try {
      const saved = await savePromotion({ ...draft, link: draft.link?.trim() || undefined });
      setLive(saved);
      setStatus(draft.active ? 'Saved and live on the next app open.' : 'Saved. Switch it on when ready.');
    } catch (e) {
      setStatus(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm('Take the promotion down? Nobody will see it after their next app open.')) return;
    setBusy(true);
    try {
      await clearPromotion();
      setLive(null);
      setDraft(blank());
      setStatus('Removed.');
    } catch (e) {
      setStatus(`Could not remove: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!draft) return <p className="p-6 text-sm text-gray-500">Loading…</p>;

  const now = Date.now();
  const start = Date.parse(draft.startAt);
  const end = Date.parse(draft.endAt);
  const state = !live
    ? { text: 'Nothing is running', tone: 'bg-gray-100 text-gray-600' }
    : !live.active
      ? { text: 'Switched off', tone: 'bg-gray-100 text-gray-600' }
      : now < start
        ? { text: 'Scheduled', tone: 'bg-amber-100 text-amber-800' }
        : now >= end
          ? { text: 'Finished', tone: 'bg-gray-100 text-gray-600' }
          : { text: 'Live now', tone: 'bg-green-100 text-green-800' };

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promotion</h1>
          <p className="mt-1 text-sm text-gray-500">
            A full-screen notice over the app on open. One runs at a time.
          </p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${state.tone}`}>{state.text}</span>
      </div>

      <div className="mt-6 space-y-5 rounded-xl border border-gray-200 bg-white p-5">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Name</span>
          <span className="ml-2 text-xs text-gray-400">for your reference; never shown in the app</span>
          <input
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Diwali 2026 greeting"
            className="mt-1 w-full rounded-lg border-gray-300 text-sm"
          />
        </label>

        {/* The artwork carries the whole message, so there is nothing else to
            write here -- and one picture per language rather than one shared,
            because the message itself is in the picture. */}
        {(['mr', 'en'] as const).map((lang) => (
          <div key={lang} className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {lang === 'mr' ? 'Picture · मराठी' : 'Picture · English'}
                {lang === 'en' && <span className="ml-2 text-xs text-gray-400">optional; Marathi is used when absent</span>}
              </span>
              {draft.image[lang] && (
                <button type="button" onClick={() => setPreview(preview === lang ? null : lang)} className="text-xs font-semibold text-blue-700">
                  {preview === lang ? 'hide' : 'preview'}
                </button>
              )}
            </div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(lang, f);
                e.target.value = '';
              }}
              className="mt-2 text-xs"
            />
            {progress[lang] > 0 && <span className="ml-2 text-xs text-gray-500">{progress[lang]}%</span>}
            {preview === lang && draft.image[lang] && (
              <img src={draft.image[lang]} alt="" className="mt-3 max-h-80 rounded-lg border border-gray-200" />
            )}
          </div>
        ))}

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Starts</span>
            <input
              type="datetime-local"
              value={isoToLocal(draft.startAt)}
              onChange={(e) => set('startAt', localToIso(e.target.value))}
              className="mt-1 w-full rounded-lg border-gray-300 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Ends</span>
            <input
              type="datetime-local"
              value={isoToLocal(draft.endAt)}
              onChange={(e) => set('endAt', localToIso(e.target.value))}
              className="mt-1 w-full rounded-lg border-gray-300 text-sm"
            />
          </label>
        </div>
        <p className="-mt-2 text-xs text-gray-400">
          Your own clock. Everyone sees the promotion at the same moment, whatever zone their phone is set to.
        </p>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Opens on tap</span>
          <span className="ml-2 text-xs text-gray-400">optional — /dnyankiran, /video, or an https link</span>
          <input
            value={draft.link ?? ''}
            onChange={(e) => set('link', e.target.value)}
            placeholder="leave empty to make the picture not tappable"
            className="mt-1 w-full rounded-lg border-gray-300 text-sm"
          />
        </label>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={draft.active} onChange={(e) => set('active', e.target.checked)} className="rounded" />
          <span className="text-sm font-medium text-gray-700">Switched on</span>
        </label>

        <div className="flex items-center gap-3 border-t border-gray-100 pt-4">
          <button onClick={() => void save()} disabled={busy} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            Save
          </button>
          <button onClick={() => { setDraft(blank()); setStatus('New promotion started. Anyone who closed the last one will see this one.'); }} disabled={busy} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
            New promotion
          </button>
          {live && (
            <button onClick={() => void remove()} disabled={busy} className="ml-auto flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-red-600">
              <TrashIcon className="h-4 w-4" /> Remove
            </button>
          )}
        </div>
        {status && <p className="text-sm text-gray-600">{status}</p>}
      </div>

      <p className="mt-4 text-xs leading-relaxed text-gray-500">
        Someone who closes the promotion will not see it again until it ends. Changes reach a phone on its
        next app open, not while it is running. Keep the picture under 1.5&nbsp;MB: every device downloads
        it once, and a GIF grows quickly. Leave the top-right corner clear — about 90&nbsp;px in a
        1080-wide picture — because the close button sits there.
      </p>
    </div>
  );
}
